// /viability/scenarios — Monte Carlo scenario bands from /api/ai/viability-scenarios.
//
// Replaces the 60-yr point forecast with p10/p50/p90 bands over 10 years
// across three scenarios (status_quo, forge_targets_met, forge_targets_missed).
// See src/worker/ai/viability-scenarios.ts (Unit 15).

import { useEffect, useState } from "react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Area,
  ComposedChart,
  ResponsiveContainer,
} from "recharts";

interface TrajectoryPoint {
  year_offset: number;
  p10: number;
  p50: number;
  p90: number;
}

interface ScenarioParams {
  retention_year1: number;
  applicants_per_year_mean: number;
  applicants_per_year_sd: number;
  bod_approval_rate: number;
  retention_year2plus: number;
  retention_sd: number;
}

interface Scenario {
  key: string;
  label: string;
  description: string;
  params: ScenarioParams;
  trajectory: TrajectoryPoint[];
}

interface ViabilityResponse {
  computed_at: string;
  starting_active: number;
  applicants_ytd_observed: number;
  retention_ytd_observed: number;
  scenarios: Scenario[];
  methodology: string;
  error?: string;
}

const SCENARIO_COLORS: Record<string, { line: string; fill: string }> = {
  status_quo: { line: "#6b7280", fill: "rgba(107,114,128,0.15)" },
  forge_targets_met: { line: "#10b981", fill: "rgba(16,185,129,0.18)" },
  forge_targets_missed: { line: "#ef4444", fill: "rgba(239,68,68,0.18)" },
};

export default function ViabilityScenarios() {
  const [data, setData] = useState<ViabilityResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    fetch("/api/ai/viability-scenarios", { credentials: "same-origin" })
      .then((r) =>
        r.ok
          ? (r.json() as Promise<ViabilityResponse>)
          : Promise.reject(new Error(`HTTP ${r.status}`)),
      )
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setSelected(new Set((d.scenarios ?? []).map((s) => s.key)));
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (err) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-red-600">
        Failed to load: {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="glass rounded-xl p-6 text-sm text-theme-muted">
        Loading scenarios…
      </div>
    );
  }
  if (data.error) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="glass rounded-xl p-6 text-sm text-amber-700">
          {data.error}
        </div>
      </div>
    );
  }

  const scenarios = data.scenarios ?? [];
  const visible = scenarios.filter((s) => selected.has(s.key));

  // Build a combined chart dataset with one row per year_offset, columns
  // per scenario. Recharts handles the Area + Line overlays naturally.
  const yearOffsets = Array.from(
    new Set(scenarios.flatMap((s) => s.trajectory.map((t) => t.year_offset))),
  ).sort((a, b) => a - b);
  const chartData = yearOffsets.map((yr) => {
    const row: Record<string, number> = { year_offset: yr };
    for (const s of visible) {
      const t = s.trajectory.find((p) => p.year_offset === yr);
      if (t) {
        row[`${s.key}_p50`] = t.p50;
        row[`${s.key}_low`] = t.p10;
        row[`${s.key}_high`] = t.p90;
        row[`${s.key}_bandWidth`] = t.p90 - t.p10;
      }
    }
    return row;
  });

  return (
    <div className="space-y-6 px-4 md:px-8 py-6 max-w-7xl mx-auto">
      <header>
        <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-brand-adaptive mb-1">
          Retention · Viability
        </p>
        <h1 className="text-2xl font-bold text-theme-primary tracking-tight">
          Scenario bands (10-year horizon)
        </h1>
        <p className="text-sm text-theme-secondary mt-1">
          Monte Carlo trajectories of active headcount across three scenarios.
          Shaded band = p10-to-p90. Line = p50 (median outcome). Replaces the
          prior 60-year point forecast — bands honest about uncertainty are the
          responsible alternative.
        </p>
      </header>

      <section className="glass rounded-2xl p-5 md:p-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="text-xs text-theme-muted space-x-4">
            <span>
              <strong className="text-theme-primary">
                {data.starting_active.toLocaleString()}
              </strong>{" "}
              starting active
            </span>
            <span>
              <strong className="text-theme-primary">
                {data.applicants_ytd_observed.toLocaleString()}
              </strong>{" "}
              applicants YTD
            </span>
            <span>
              <strong className="text-theme-primary">
                {(data.retention_ytd_observed * 100).toFixed(1)}%
              </strong>{" "}
              year-1 retention
            </span>
          </div>
          <div className="ml-auto flex flex-wrap gap-2">
            {scenarios.map((s) => (
              <label
                key={s.key}
                className="inline-flex items-center gap-2 text-xs"
              >
                <input
                  type="checkbox"
                  checked={selected.has(s.key)}
                  onChange={(e) => {
                    const next = new Set(selected);
                    if (e.target.checked) next.add(s.key);
                    else next.delete(s.key);
                    setSelected(next);
                  }}
                />
                <span
                  className="inline-block w-3 h-3 rounded-full"
                  style={{ background: SCENARIO_COLORS[s.key]?.line ?? "#999" }}
                />
                <span className="text-theme-secondary">{s.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ width: "100%", height: 380 }}>
          <ResponsiveContainer>
            <ComposedChart
              data={chartData}
              margin={{ top: 8, right: 24, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                stroke="#e5e7eb"
                strokeDasharray="3 3"
                opacity={0.4}
              />
              <XAxis
                dataKey="year_offset"
                label={{
                  value: "Years out",
                  position: "insideBottom",
                  offset: -4,
                  fontSize: 11,
                }}
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {visible.map((s) => {
                const palette = SCENARIO_COLORS[s.key] ?? {
                  line: "#666",
                  fill: "rgba(0,0,0,0.1)",
                };
                return [
                  <Area
                    key={`${s.key}-band`}
                    type="monotone"
                    dataKey={`${s.key}_high`}
                    stackId={s.key}
                    stroke="none"
                    fill={palette.fill}
                    name={`${s.label} p90`}
                  />,
                  <Area
                    key={`${s.key}-band-lo`}
                    type="monotone"
                    dataKey={`${s.key}_low`}
                    stackId={s.key}
                    stroke="none"
                    fill="#fff"
                    fillOpacity={0}
                    name={`${s.label} p10`}
                  />,
                  <Line
                    key={`${s.key}-med`}
                    type="monotone"
                    dataKey={`${s.key}_p50`}
                    stroke={palette.line}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name={s.label}
                  />,
                ];
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 space-y-3 text-xs">
          {visible.map((s) => (
            <div key={s.key} className="flex items-start gap-3">
              <span
                className="inline-block w-3 h-3 rounded-full mt-1 shrink-0"
                style={{ background: SCENARIO_COLORS[s.key]?.line }}
              />
              <div>
                <p className="font-semibold text-theme-primary">{s.label}</p>
                <p className="text-theme-muted">{s.description}</p>
                <p className="text-theme-muted font-mono tabular-nums mt-0.5">
                  {s.trajectory
                    .map(
                      (t) => `yr${t.year_offset}: ${t.p10}-${t.p50}-${t.p90}`,
                    )
                    .join(" · ")}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="glass rounded-xl p-5 mt-6">
        <h2 className="text-base font-semibold text-theme-primary mb-3">
          If we're tracking…
        </h2>
        <p className="text-sm text-theme-secondary mb-4">
          Bands without decisions are academic. These are the board-level moves
          to consider at each outcome level — calibrated against the Q1-2027
          FORGE targets.
        </p>
        <ul className="space-y-3 text-sm">
          <li className="flex items-start gap-3">
            <span className="inline-block w-3 h-3 rounded-full mt-1.5 shrink-0 bg-emerald-500" />
            <div>
              <p className="font-semibold text-theme-primary">…the p90 band</p>
              <p className="text-theme-muted">
                We're at or above the optimistic case. Hold current strategy;
                redirect attention to building the next bench (officer pipeline,
                training cadre depth) rather than recruitment volume.
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="inline-block w-3 h-3 rounded-full mt-1.5 shrink-0 bg-zinc-400" />
            <div>
              <p className="font-semibold text-theme-primary">
                …the p50 (median) band
              </p>
              <p className="text-theme-muted">
                On-track for Q1-2027 targets but no margin. Quarterly review of
                FORGE intervention effectiveness; Treasurer confirms recruitment
                budget covers the next four cohorts.
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="inline-block w-3 h-3 rounded-full mt-1.5 shrink-0 bg-red-500" />
            <div>
              <p className="font-semibold text-theme-primary">…the p10 band</p>
              <p className="text-theme-muted">
                We're tracking the bad case. Emergency BOD review: examine
                retention root-cause data, authorise a structural intervention
                (recruitment-budget step-up, mentorship program rebuild, or
                scope reduction). This is a governance trigger, not a staff
                project.
              </p>
            </div>
          </li>
        </ul>
        <p className="text-xs text-theme-muted mt-4 italic">
          Triggers are advisory — the board sets policy, this page surfaces the
          data.
        </p>
      </section>

      <footer className="text-xs text-theme-muted">
        <p className="italic">{data.methodology}</p>
        <p className="mt-1">
          Computed: {new Date(data.computed_at).toLocaleString()}
        </p>
      </footer>
    </div>
  );
}
